import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Trash2, X } from "lucide-react";

import { api, formatApiError } from "@/api/client";

export type BulkEntity = "person" | "group" | "program" | "room" | "lead";
export type BulkItem = { id: string; tenant_id?: string | null; tenant_name?: string | null };
type Outcome = { status: string; reason?: string };
type ResultRow = Outcome & { id: string };

export const BULK_MAX = 100;
const PATH: Record<BulkEntity, string> = {
    person: "people", group: "groups", program: "programs", room: "rooms", lead: "leads",
};

export function useBulkSelection(resetKey: string) {
    const [sel, setSel] = useState<Map<string, BulkItem>>(new Map());
    const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
    useEffect(() => { setSel(new Map()); setOutcomes({}); }, [resetKey]);
    const toggle = (it: BulkItem) => setSel(prev => {
        const m = new Map(prev);
        if (m.has(it.id)) m.delete(it.id); else m.set(it.id, it);
        return m;
    });
    const toggleAll = (rows: BulkItem[]) => setSel(prev => {
        const m = new Map(prev);
        const all = rows.length > 0 && rows.every(r => m.has(r.id));
        rows.forEach(r => (all ? m.delete(r.id) : m.set(r.id, r)));
        return m;
    });
    const clear = () => { setSel(new Map()); setOutcomes({}); };
    return { sel, outcomes, setSel, setOutcomes, toggle, toggleAll, clear };
}
export type BulkSelection = ReturnType<typeof useBulkSelection>;

function useReasonText() {
    const { t, i18n } = useTranslation();
    return (o: Outcome) => (o.reason && i18n.exists(`trash.delete_blocker.${o.reason}`)
        ? t(`trash.delete_blocker.${o.reason}`)
        : t(`bulk.reason.${o.status}`, t("bulk.reason.error")));
}

export function SelectAllCheckbox({ selection, rows, testid }: { selection: BulkSelection; rows: BulkItem[]; testid: string }) {
    const { t } = useTranslation();
    const ref = useRef<HTMLInputElement>(null);
    const n = rows.filter(r => selection.sel.has(r.id)).length;
    useEffect(() => { if (ref.current) ref.current.indeterminate = n > 0 && n < rows.length; }, [n, rows.length]);
    return (
        <input ref={ref} type="checkbox" data-testid={testid} aria-label={t("bulk.select_all")} title={t("bulk.select_all")}
            className="h-4 w-4 cursor-pointer accent-[var(--primary)]" disabled={rows.length === 0}
            checked={rows.length > 0 && n === rows.length}
            onClick={(e) => e.stopPropagation()} onChange={() => selection.toggleAll(rows)} />
    );
}

export function RowCheckbox({ selection, item, testid }: { selection: BulkSelection; item: BulkItem; testid: string }) {
    const { t } = useTranslation();
    const reasonText = useReasonText();
    const o = selection.outcomes[item.id];
    return (
        <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" data-testid={testid} aria-label={t("bulk.select_row")}
                className="h-4 w-4 cursor-pointer accent-[var(--primary)]"
                checked={selection.sel.has(item.id)} onChange={() => selection.toggle(item)} />
            {o && selection.sel.has(item.id) && (
                <span className="text-[11px] leading-tight min-w-[160px] max-w-[240px]" style={{ color: "var(--error-fg)" }}
                    data-testid={`${testid}-reason`}>{reasonText(o)}</span>
            )}
        </span>
    );
}

interface BarProps {
    selection: BulkSelection;
    entityType: BulkEntity;
    platform?: boolean;
    onDone: () => void;
    testidPrefix?: string;
    mode?: "archive" | "purge";
}

export function BulkActionBar({ selection, entityType, platform = false, onDone, testidPrefix = "bulk", mode = "archive" }: BarProps) {
    const purge = mode === "purge";
    const { t } = useTranslation();
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const items = useMemo(() => Array.from(selection.sel.values()), [selection.sel]);
    const count = items.length;
    const over = count > BULK_MAX;

    const perTenant = useMemo(() => {
        const m = new Map<string, { name: string; n: number }>();
        for (const it of items) {
            const k = it.tenant_id || "?";
            const cur = m.get(k) || { name: it.tenant_name || t("bulk.unknown_tenant"), n: 0 };
            cur.n += 1; m.set(k, cur);
        }
        return Array.from(m.entries());
    }, [items, t]);

    if (count === 0) return null;

    const run = async () => {
        setBusy(true);
        try {
            const { data } = purge
                ? await api.post("/trash/bulk-purge", { batch_ids: items.map(i => i.id) }).then(r => ({
                    data: {
                        deleted: r.data.purged, skipped: r.data.failed,
                        results: (r.data.results || []).map((x: any) => ({
                            id: x.batch_id, status: x.ok ? "deleted" : x.error === "not_found" ? "not_found" : "blocked",
                            reason: x.error,
                        })),
                    },
                }))
                : platform
                ? await api.post("/platform/bulk-archive", {
                    entity_type: entityType,
                    items: items.map(i => ({ id: i.id, tenant_id: i.tenant_id })),
                })
                : await api.post(`/${PATH[entityType]}/bulk-archive`, { ids: items.map(i => i.id) });
            const results: ResultRow[] = data.results || [];
            const next = new Map(selection.sel);
            const outcomes: Record<string, Outcome> = {};
            for (const r of results) {
                if (r.status === "deleted") next.delete(r.id);
                else outcomes[r.id] = { status: r.status, reason: r.reason };
            }
            selection.setSel(next);
            selection.setOutcomes(outcomes);
            const msg = t(purge ? "trash.purge.bulk_result" : "bulk.result", { deleted: data.deleted ?? 0, skipped: data.skipped ?? 0 });
            if ((data.skipped ?? 0) === 0) toast.success(msg); else toast.warning(msg);
            setConfirming(false);
            onDone();
        } catch (e: any) {
            toast.error(`${t("bulk.failed")} ${formatApiError(e) || ""}`.trim());
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 rounded-xl border px-4 py-3 shadow-lg"
                style={{ background: "var(--card)", borderColor: "var(--border)", color: "var(--text)" }}
                data-testid={`${testidPrefix}-bar`}>
                <span className="text-sm font-medium" data-testid={`${testidPrefix}-count`}>{t("bulk.selected", { count })}</span>
                {over && <span className="text-xs" style={{ color: "var(--error-fg)" }} data-testid={`${testidPrefix}-max-hint`}>{t("bulk.max_hint", { max: BULK_MAX })}</span>}
                <button type="button" disabled={over || busy} onClick={() => setConfirming(true)}
                    title={over ? t("bulk.max_hint", { max: BULK_MAX }) : undefined}
                    className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50 transition-opacity"
                    style={{ background: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }} data-testid={`${testidPrefix}-delete`}>
                    <Trash2 size={14} /> {t(purge ? "trash.purge.delete_selected" : "bulk.delete_selected")}
                </button>
                <button type="button" onClick={selection.clear} disabled={busy}
                    className="rounded-md px-3 py-1.5 text-sm border transition-colors hover:bg-[var(--input)]"
                    style={{ borderColor: "var(--border)" }} data-testid={`${testidPrefix}-cancel`}>{t("bulk.cancel")}</button>
            </div>
            {confirming && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
                    onClick={() => !busy && setConfirming(false)} data-testid={`${testidPrefix}-dialog`}>
                    <div className="surface-card w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between">
                            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t(purge ? "trash.purge.confirm_title" : "bulk.confirm_title")}</h2>
                            <button onClick={() => setConfirming(false)} disabled={busy} style={{ color: "var(--text-muted)" }}
                                data-testid={`${testidPrefix}-dialog-close`}><X size={18} /></button>
                        </div>
                        <p className="text-sm" style={{ color: "var(--text)" }} data-testid={`${testidPrefix}-dialog-body`}>{t(purge ? "trash.purge.bulk_confirm_body" : "bulk.confirm_body", { count })}</p>
                        {platform && (
                            <div className="rounded-md p-3 text-sm" style={{ background: "var(--input)" }} data-testid={`${testidPrefix}-per-tenant`}>
                                <div className="font-medium mb-2" style={{ color: "var(--text)" }}>{t("bulk.per_tenant")}</div>
                                <ul className="space-y-1 text-xs" style={{ color: "var(--text-muted)" }}>
                                    {perTenant.map(([tid, v]) => (
                                        <li key={tid} className="flex justify-between gap-4" data-testid={`${testidPrefix}-tenant-${tid}`}>
                                            <span>{v.name}</span><b>{v.n}</b>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="flex justify-end gap-3 pt-2">
                            <button type="button" className="px-4 py-2 rounded-md border text-sm" disabled={busy}
                                style={{ borderColor: "var(--border)", color: "var(--text)" }}
                                onClick={() => setConfirming(false)} data-testid={`${testidPrefix}-dialog-cancel`}>{t("bulk.cancel")}</button>
                            <button type="button" disabled={busy} onClick={run}
                                className="px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                                style={{ background: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }} data-testid={`${testidPrefix}-dialog-confirm`}>
                                {busy && <Loader2 className="animate-spin" size={14} />} {t(purge ? "trash.purge.confirm_btn" : "bulk.confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
